import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ICostCenter } from '../../../../common/data/dtos/coster-center';
import { IEmployee } from '../../../../common/data/dtos/employee';
import { IJobTitle } from '../../../../common/data/dtos/job-title';
import { IPermission } from '../../../../common/data/dtos/permission';
import DriverLicenseEnum from '../../../../common/data/enums/enumDriverLicense';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import Select from '../../../../components/bootstrap/forms/Select';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showError, showSuccess, showWarning } from '../../../../components/extras/Notifications';
import { generateComplexPassword, isValidCPF, onlyNumbers } from '../../../../helpers/helpers';
import authService from '../../../../services/auth-service';
import costCenterService from '../../../../services/cost-center-service';
import employeeService from '../../../../services/employee-service';
import jobTitleService from '../../../../services/job-title-service';
import permissionService from '../../../../services/permission-service';

type EmployeeFormValues = {
    id?: string;
    name: string;
    registrationNumber: string;
    document: string;
    socialIntegration: string;
    birthDate?: string;
    admissionDate?: string;
    dismissalDate?: string;
    driverLicense: string;
    driverLicenseExpirationDate: string;
    driverLicenseType: string;
    costCenterId: string;
    jobTitleId: string;
    permissionId: string;
    email: string;
    firstAccess: boolean;
    ohcAdmissionDate?: string;
    ohcPeriodicalDate?: string;
};

interface Props {
    isModalOpened: boolean;
    selectedEmployee: IEmployee | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const EmployeeModal = ({ isModalOpened, selectedEmployee, onModalClose, onAfterSubmit }: Props) => {
    const [costCenters, setCostCenters] = useState<ICostCenter[]>([]);
    const [jobTitles, setJobTitles] = useState<IJobTitle[]>([]);
    const [permissions, setPermissions] = useState<IPermission[]>([]);
    const [filteredJobTitles, setFilteredJobTitles] = useState<IJobTitle[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            name: '',
            registrationNumber: '',
            document: '',
            socialIntegration: '',
            birthDate: '',
            admissionDate: '',
            dismissalDate: '',
            driverLicense: '',
            driverLicenseExpirationDate: '',
            driverLicenseType: '0',
            costCenterId: '',
            jobTitleId: '',
            permissionId: '',
            email: '',
            firstAccess: true,
            ohcAdmissionDate: '',
            ohcPeriodicalDate: '',
        },

        validate: (values) => {
            const errors: {
                name?: string;
                document?: string;
                permissionId?: string;
                email?: string;
            } = {};

            if (!values.name) errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;

            if (!values.document) errors.document = MESSAGES.VALIDATION.REQUIRED_FIELD;
            else {
                const isValidDocument = isValidCPF(values.document);
                if (!isValidDocument) errors.document = 'CPF inválido';
            }

            if (!values.permissionId) 
                errors.permissionId = MESSAGES.VALIDATION.REQUIRED_FIELD;

            if (!values.email)
                errors.email = MESSAGES.VALIDATION.REQUIRED_FIELD;
            else {
                const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                const isValid = emailRegex.test(values.email);

                if (!isValid)
                    errors.email = 'Formato inválido';
            }

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {
        resetForm();
        fetchData();

        if (selectedEmployee) setFieldValues(selectedEmployee);
    }, [isModalOpened]);

    const onFormSubmit = async (values: EmployeeFormValues) => {
        if (selectedEmployee) {
            setIsSubmitting(true);

            const driverLicenseType: DriverLicenseEnum = DriverLicenseEnum[values.driverLicenseType as keyof typeof DriverLicenseEnum];

            const response = await employeeService.updateEmployee({
                id: selectedEmployee.id,
                name: values.name,
                registrationNumber: values.registrationNumber,
                document: onlyNumbers(values.document),
                socialIntegration: values.socialIntegration,
                birthDate: values.birthDate ? dayjs(values.birthDate).toDate() : undefined,
                admissionDate: values.admissionDate ? dayjs(values.admissionDate).toDate() : undefined,
                dismissalDate: values.dismissalDate ? dayjs(values.dismissalDate).toDate() : undefined,
                driverLicense: values.driverLicense,
                driverLicenseExpirationDate: values.driverLicenseExpirationDate ? dayjs(values.driverLicenseExpirationDate).toDate() : undefined,
                driverLicenseType,
                costCenterId: values.costCenterId,
                jobTitleId: values.jobTitleId,
                permissionId: values.permissionId,
                email: values.email,
                firstAccess: values.firstAccess,
                ohcAdmissionDate: values.ohcAdmissionDate ? dayjs(values.ohcAdmissionDate).toDate() : undefined,
                ohcPeriodicalDate: values.ohcPeriodicalDate ? dayjs(values.ohcPeriodicalDate).toDate() : undefined,
                rowVersion: selectedEmployee.rowVersion,
                status: selectedEmployee.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const driverLicenseType: DriverLicenseEnum = DriverLicenseEnum[values.driverLicenseType as keyof typeof DriverLicenseEnum];

            const response = await employeeService.createEmployee({
                name: values.name,
                registrationNumber: values.registrationNumber,
                document: onlyNumbers(values.document),
                socialIntegration: values.socialIntegration,
                birthDate: values.birthDate ? dayjs(values.birthDate).toDate() : undefined,
                admissionDate: values.admissionDate ? dayjs(values.admissionDate).toDate() : undefined,
                dismissalDate: values.dismissalDate ? dayjs(values.dismissalDate).toDate() : undefined,
                driverLicense: values.driverLicense,
                driverLicenseExpirationDate: values.driverLicenseExpirationDate ? dayjs(values.driverLicenseExpirationDate).toDate() : undefined,
                driverLicenseType,
                costCenterId: values.costCenterId,
                jobTitleId: values.jobTitleId,
                permissionId: values.permissionId,
                email: values.email,
                firstAccess: values.firstAccess,
                ohcAdmissionDate: values.ohcAdmissionDate ? dayjs(values.ohcAdmissionDate).toDate() : undefined,
                ohcPeriodicalDate: values.ohcPeriodicalDate ? dayjs(values.ohcPeriodicalDate).toDate() : undefined,
            });

            if (response.success) {
                const cognitoResponse = await authService.createCognitoUser(values.email, generateComplexPassword(12));
                if (!cognitoResponse.success) {
                    if (response.employee && response.employee.id) {
                        const deleteEmployeeResponse = await employeeService.deleteEmployee(response.employee.id);
                        if (deleteEmployeeResponse.success) {
                            showWarning(MESSAGES.ACTION.SAVE_ERROR);
                            return;
                        } else {
                            showError(MESSAGES.ACTION.GENERALIZED_ERRORS);
                        }
                    }
                }
            }

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: '',
                registrationNumber: '',
                document: '',
                socialIntegration: '',
                birthDate: '',
                admissionDate: '',
                dismissalDate: '',
                driverLicense: '',
                driverLicenseExpirationDate: '',
                driverLicenseType: '0',
                costCenterId: '',
                jobTitleId: '',
                permissionId: '',
                email: '',
                firstAccess: true,
                ohcAdmissionDate: '',
                ohcPeriodicalDate: ''
            },
        });
    };

    const setFieldValues = (values: IEmployee) => {
        formik.setFieldValue('name', values.name || '');
        formik.setFieldValue('registrationNumber', values.registrationNumber || '');
        formik.setFieldValue('document', values.document || '');
        formik.setFieldValue('socialIntegration', values.socialIntegration || '');
        formik.setFieldValue('birthDate', values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('admissionDate', values.admissionDate ? dayjs(values.admissionDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('dismissalDate', values.dismissalDate ? dayjs(values.dismissalDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('driverLicense', values.driverLicense || '');
        formik.setFieldValue('driverLicenseExpirationDate', values.driverLicenseExpirationDate ? dayjs(values.driverLicenseExpirationDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('driverLicenseType', values.driverLicenseType.toString());
        formik.setFieldValue('costCenterId', values.costCenterId || '');
        formik.setFieldValue('jobTitleId', values.jobTitleId || '');
        formik.setFieldValue('permissionId', values.permissionId || '');
        formik.setFieldValue('email', values.email);
        formik.setFieldValue('firstAccess', values.firstAccess);
        formik.setFieldValue('birthDate', values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('ohcAdmissionDate', values.ohcAdmissionDate ? dayjs(values.ohcAdmissionDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('ohcPeriodicalDate', values.ohcPeriodicalDate ? dayjs(values.ohcPeriodicalDate).format('YYYY-MM-DD') : '');
    };

    const fetchData = async () => {
        setIsSubmitting(true);

        try {
            const [costCentersResponse, jobTitlesResponse, permissionsResponse] = await Promise.all([costCenterService.getCostCenters(), jobTitleService.getJobTitles(), permissionService.getPermissions()]);

            if (costCentersResponse.error) {
                showWarning(costCentersResponse.error);
                return;
            }

            if (jobTitlesResponse.error) {
                showWarning(jobTitlesResponse.error);
                return;
            }

            if (permissionsResponse.error) {
                showWarning(permissionsResponse.error);
                return;
            }

            setCostCenters(costCentersResponse.costCenters || []);
            setJobTitles(jobTitlesResponse.jobTitles || []);
            setPermissions(permissionsResponse.permissions || []);
            setFilteredJobTitles(jobTitlesResponse.jobTitles || []);
        } catch (error) {
            showWarning('Erro ao carregar dados');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onCostCenterChange = (costCenterId: string) => {
        const filteredList = jobTitles.filter((item) => item.costCenterId === costCenterId);
        if (filteredList && filteredList.length > 0) {
            setFilteredJobTitles(filteredList);
        }
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size="lg" isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="employee-modal">Manutenção de colaboradores</ModalTitle>
                {formik.values.firstAccess && (
                    <Badge color="primary" className="ms-2">
                        Primeiro Acesso
                    </Badge>
                )}
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title="Dados pessoais" icon="Person">
                        <div className="row g-4">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="name" label="Nome" isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=""
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="email" label="E-mail" isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.email}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.email}
                                        invalidFeedback={formik.errors.email}
                                        validFeedback=""
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="registrationNumber" label="Matrícula" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.registrationNumber} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="document" label="CPF" isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.document}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.document}
                                        invalidFeedback={formik.errors.document}
                                        validFeedback=""
                                        mask="999.999.999-99"
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="socialIntegration" label="PIS" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.socialIntegration} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="birthDate" label="Data de Nascimento" isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.birthDate} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="costCenterId" label="Centro de Custo" isFloating>
                                    <Select
                                        ariaLabel="Escolha um centro de custo"
                                        onBlur={formik.handleBlur}
                                        value={formik.values.costCenterId}
                                        list={costCenters.map((costCenter) => ({
                                            value: costCenter.id,
                                            text: `${costCenter.description}`,
                                        }))}
                                        onChange={(e: { target: { value: string } }) => {
                                            formik.handleChange(e);
                                            onCostCenterChange(e.target.value);
                                        }}
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="jobTitleId" label="Função" isFloating>
                                    <Select
                                        ariaLabel="Escolha uma função"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.jobTitleId}
                                        list={filteredJobTitles.map((jobTitle) => ({
                                            value: jobTitle.id,
                                            text: `${jobTitle.name}`,
                                        }))}
                                    />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="admissionDate" label="Data de Admissão" isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.admissionDate} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="dismissalDate" label="Data de Demissão" isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.dismissalDate} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="permissionId" label="Permissão" isFloating>
                                    <Select
                                        ariaLabel="Escolha uma permissão"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.permissionId}
                                        list={
                                            permissions.map((permission) => ({
                                                value: permission.id,
                                                text: `${permission.name}`
                                            }))
                                        }
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.permissionId}
                                        invalidFeedback={formik.errors.permissionId}
                                        validFeedback=""

                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>

                    <CardTabItem id="tab-item-2" title="Dados da CNH" icon="DirectionsCar">
                        <div className="row g-4">
                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="driverLicense" label="Número" isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.driverLicense} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="driverLicenseExpirationDate" label="Vencimento" isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.driverLicenseExpirationDate} />
                                </FormGroup>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-12">
                                <FormGroup id="driverLicenseType" label="Categoria" isFloating>
                                    <Select
                                        ariaLabel="Escolha uma categoria"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.driverLicenseType}
                                        list={[
                                            { value: '0', text: 'Nenhuma' },
                                            { value: '1', text: 'Categoria A' },
                                            { value: '2', text: 'Categoria B' },
                                            { value: '3', text: 'Categoria C' },
                                            { value: '4', text: 'Categoria D' },
                                            { value: '5', text: 'Categoria E' },
                                        ]}
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>

                    <CardTabItem id='tab-item-1' title='ASO' icon='Article'>
                        <div className='row g-4'>
                            <div className='col-lg-6 col-md-6 col-sm-12'>

                                <FormGroup id='ohcAdmissionDate' label='Data ASO Admissional' isFloating>
                                    <Input
                                        type="date"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.ohcAdmissionDate} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='ohcPeriodicalDate' label='Data ASO Periódico' isFloating>
                                    <Input
                                        type="date"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.ohcPeriodicalDate} />
                                </FormGroup>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};
